import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { Component } from 'react';
import { RouteComponentProps, StaticContext, withRouter } from 'react-router';
import CheckoutRefund from '../../components/CheckoutRefund/CheckoutRefund';
import OrderCartTotalInfo from '../../components/OrderCartTotalInfo/OrderCartTotalInfo';
import OrderMiniMap from '../../components/OrderMiniMap/OrderMiniMap';
import { discardCb } from '../../helpers/checkout';
import OrderCartProducts from '../OrderCartProducts/OrderCartProducts';
import SummaryDetails from '../SummaryDetails/SummaryDetails';
import { DELIVERY_TYPE } from 'src/constants/deliveryTypes';
import { PAYMENT_TYPES } from 'src/constants/main';
import { USER_PERMISSIONS } from 'src/constants/user';
import { ORDER_EDIT_STAGE } from 'src/mobx/checkoutStore/constants';
import { localeStore } from 'src/mobx/localesStore';
import { TOrder } from 'src/types/orders';
import { hasPermissions } from 'src/utils/helpers';
import { confirmDelete, leaveWithoutSaving } from 'src/utils/modalConfirmations';
import Button from 'src/view/components/Controls/ButtonComponent/Button';
import ModalPopup from 'src/view/components/ModalPopup/ModalPopup';
import TabList from 'src/view/components/Tabs/TabList';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';
import { CheckoutStore } from '~/mobx/checkoutStore';
import { modalStore } from '~/mobx/modalStore';
import { CallCenterStore } from '~/mobx/ordersStore/callCenterStore';
import { messagesStore } from '~/view/containers/Messages';
import getOrderViewPath from '~/view/screens/CheckoutScreen/getOrderViewPath';
import getOrdersPath from '~/view/screens/CheckoutScreen/getOrdersPath';
import './style.scss';
import { ORDER_STATUS } from '~/constants/order';
import { ButtonAppearance, TButtonAppearance } from '~/view/components/Controls/ButtonComponent/types';
import { appStore } from '~/mobx/appStore';

type TProps = RouteComponentProps<{ id: string }, StaticContext, TOrder | undefined> & { checkoutStore: CheckoutStore };

interface IState {
  message: string;
}

@observer
class OrderCreateSidebar extends Component<TProps, IState> {
  modal: ModalPopup | null = null;
  state = {
    message: '',
  };

  get checkoutStore() {
    return this.props.checkoutStore;
  }

  openMessagePopup = () => this.modal?.openModalPopup();
  closeMessagePopup = () => this.modal?.closeModalPopup();
  displayMessage = (message: string) => {
    this.setState({ message }, this.openMessagePopup);
  };

  showDiscardModal = () => {
    if (this.checkoutStore.parentStore instanceof CallCenterStore && this.checkoutStore.isDraft) {
      confirmDelete({
        title: localeStore.t('delete_draft'),
        message: localeStore.t('draft_will_be_deleted'),
        onConfirm: () => discardCb(this.checkoutStore),
      });
      return;
    }

    leaveWithoutSaving({ onConfirm: () => discardCb(this.checkoutStore) });
  };

  continue = async () => {
    let successfully = false;
    const isOrderCompeted = this.checkoutStore.dependencies.order?.order_status_code === ORDER_STATUS.COMPLETED;
    const isCardEmpty = !this.checkoutStore.dependencies.products.length;

    // transition to next stage
    if (this.checkoutStore.orderEditStage === ORDER_EDIT_STAGE.DETAILS || this.checkoutStore.orderEditStage === ORDER_EDIT_STAGE.PRODUCTS) {
      // transition cancelation process
      if (this.checkoutStore.orderEditStage === ORDER_EDIT_STAGE.PRODUCTS && isCardEmpty && isOrderCompeted) {
        messagesStore.addMessage({
          id: 'refund-reason-message',
          Content: () => <CheckoutRefund checkoutStore={this.checkoutStore} />,
          contentClassName: 'refund-reason-message',
        });
        return;
      }

      this.checkoutStore.setOrderEditStage(this.checkoutStore.orderEditStage + 1);
      // recalculate, because next tab include cart
      this.checkoutStore.calculate();
      return;
    }

    // create, if draft, update order
    if (this.checkoutStore.isDraft) {
      successfully = await this.checkoutStore.createOrder();
    } else {
      const isValid = this.isValid();
      if (!isValid) return;

      if (this.checkoutStore.dependencies.order && this.checkoutStore.dependencies.order.payment_type === PAYMENT_TYPES.ONLINE_PAYMENT) {
        const diff = this.checkoutStore.refundPrice();
        const isRefund = diff > 0;
        const isToPay = diff < 0;

        // when editing an order with online payment, new order amount cannot be higher than old one
        if (isToPay) {
          modalStore.openErrorModal({ message: localeStore.t('order_paid_online_total_limit') });
          return;
        }

        // calling a modal for a refund
        if (isRefund && !this.checkoutStore.dependencies.refund_reason) {
          messagesStore.addMessage({
            id: 'refund-reason-message',
            Content: () => <CheckoutRefund checkoutStore={this.checkoutStore} />,
            contentClassName: 'refund-reason-message',
          });
          return;
        }
      }

      successfully = await this.checkoutStore.update(true);
    }

    if (this.checkoutStore.dependencies.order && successfully) {
      if (this.checkoutStore.parentStore instanceof CallCenterStore && this.checkoutStore.isDraft) {
        this.props.history.push(getOrdersPath(this.checkoutStore.parentStore));
      } else {
        this.props.history.push(
          getOrderViewPath(this.checkoutStore.parentStore, this.checkoutStore.dependencies.order.id),
          toJS(this.checkoutStore.dependencies.order)
        );
      }
    }
  };

  isValid = (): boolean => {
    let message = '';

    if (this.checkoutStore.onlinePaymentNewTotalMaxValid()) {
      message = localeStore.t('order_paid_online_total_limit');
    }

    if (message) {
      this.displayMessage(message);
    }

    return !message;
  };

  /**
   * Next order edit stage/create/update button disabled.
   *
   * @readonly
   * @type {boolean}
   */
  get nextStageButtonDisable(): boolean {
    const { validatorErrors: errors, isDraft } = this.checkoutStore;
    const isFinalStageAndAreErrors: boolean = Boolean(this.checkoutStore.orderEditStage === ORDER_EDIT_STAGE.CHECKOUT && Object.keys(errors).length);
    const isNoCreatePermission: boolean = isDraft && !hasPermissions(USER_PERMISSIONS.ORDERS_CREATE);

    if (errors.branch || errors.delivery_type || isFinalStageAndAreErrors || isNoCreatePermission) {
      return true;
    }

    return false;
  }

  /**
   * Next order edit stage/create/update button label.
   *
   * @readonly
   * @type {string}
   */
  get nextStageButtonLabel(): string {
    if (this.checkoutStore.orderEditStage === ORDER_EDIT_STAGE.DETAILS) return 'to_items';
    if (this.checkoutStore.orderEditStage === ORDER_EDIT_STAGE.PRODUCTS) {
      if (!this.checkoutStore.dependencies.products.length) return 'cc_emptycart_dbx_btn_cancel';
      return 'to_summary_and_checkout';
    }

    return this.checkoutStore.isDraft ? 'txt_place_order' : 'Update Order';
  }

  get nextStageButtonAppearance(): TButtonAppearance {
    if (this.checkoutStore.orderEditStage === ORDER_EDIT_STAGE.DETAILS) ButtonAppearance.PRIMARY;
    if (this.checkoutStore.orderEditStage === ORDER_EDIT_STAGE.PRODUCTS) {
      if (!this.checkoutStore.dependencies.products.length) return ButtonAppearance.DESTRUCTIVE_PRIMARY;
      return ButtonAppearance.PRIMARY;
    }

    return ButtonAppearance.PRIMARY;
  }

  /**
   * Discard button label.
   *
   * @readonly
   * @type {string}
   */
  get discardButtonLabel(): string {
    if (this.checkoutStore.isDraft) return this.checkoutStore.parentStore instanceof CallCenterStore ? 'txt_delete' : 'discard';
    return 'discard_changes';
  }

  render() {
    return (
      <div className="order-create-sidebar d-flex flex-column pr-4">
        <div className="d-flex flex-1 flex-column">
          <TabList tabs={[DELIVERY_TYPE.DELIVERY, DELIVERY_TYPE.PICKUP /* , DELIVERY_TYPE.DINE_IN */]} value={this.checkoutStore.orderEditStage}>
            {/* mini map. This block is displayed on stage "Customer details" */}
            {appStore.settings?.is_cc_zone_orders ? <div className="order-minimap" /> : <OrderMiniMap />}
            {/* total, cart  This block is displayed on stage "Items" */}
            <ScrollableBlock showScrollbar scrollbarSize="normal" className="order-create-sidebar__scroll flex-grow-1" scrollClassName="pr-3">
              <OrderCartTotalInfo />
              <span className="family-bold text-size-20 color-dark font-weight-700 mt-4">{localeStore.t('txt_cart')}</span>
              <OrderCartProducts />
            </ScrollableBlock>
            {/* summary details. This block is displayed on stage "Summary & checkout" */}
            <ScrollableBlock showScrollbar scrollbarSize="normal" className="flex-grow-1 order-create-sidebar__scroll" scrollClassName="pr-3">
              <SummaryDetails />
            </ScrollableBlock>
          </TabList>
        </div>

        {/* message modal */}
        <div className="d-flex flex-column pt-2">
          <ModalPopup
            wrapperClassName="full-width d-flex"
            vertical="top"
            horizontal="center"
            ref={(modal) => (this.modal = modal)}
            content={
              <div className="px-3 py-3 radius-14 text-center bg-blur" style={{ maxWidth: 325 }}>
                <span className="text-size-14 ">{this.state.message}</span>
              </div>
            }
          >
            {/* next order edit stage/create/update button label */}
            <Button
              title={localeStore.t(this.nextStageButtonLabel)}
              onClick={this.continue}
              disabled={this.nextStageButtonDisable}
              loading={this.checkoutStore.loading || this.checkoutStore.calculator.loading}
              appearance={this.nextStageButtonAppearance}
              size="large"
              stretched
            />
          </ModalPopup>

          {/* discard/delete button */}
          <Button
            title={localeStore.t(this.discardButtonLabel)}
            onClick={this.showDiscardModal}
            disabled={this.checkoutStore.isDraft && !hasPermissions(USER_PERMISSIONS.ORDERS_CREATE)}
            loading={this.checkoutStore.loading || this.checkoutStore.calculator.loading}
            appearance="destructive-tertiary"
            size="large"
            stretched
            className="mt-2"
          />
        </div>
      </div>
    );
  }
}

export default withRouter(OrderCreateSidebar);
